import { Fragment, useEffect, useRef, useState } from "react";
import { Check, CheckCheck, ShieldCheck, Ban, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageMedia } from "./MessageMedia";
import { dayLabel, fmtTime } from "@/lib/format";

export const DaySeparator = ({ iso }) => (
  <div className="my-3 flex justify-center">
    <span className="rounded-md bg-white/90 px-3 py-1 text-sm font-medium text-zap-ink/70 ring-1 ring-zap-line">{dayLabel(iso)}</span>
  </div>
);

const SystemBubble = ({ msg }) => (
  <div data-testid={`msg-system-${msg.id}`} className="msg-enter mx-auto my-2 flex max-w-[92%] items-start gap-2 rounded-lg border border-zap-gold/30 bg-zap-goldsoft px-4 py-3 text-[16px] leading-relaxed text-zap-ink sm:max-w-[75%]">
    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-zap-gold" />
    <span>{msg.text}<span className="ml-2 align-bottom text-xs text-zap-ink/55">{fmtTime(msg.created_at)}</span></span>
  </div>
);

export const Bubble = ({ msg, mine, client, base, footer, children }) => (
  <div className={`msg-enter flex ${mine ? "justify-end" : "justify-start"} px-3 sm:px-6`}>
    <div data-testid={`msg-${msg.id}`}
      className={`relative my-1 max-w-[86%] rounded-lg px-3 pb-1.5 pt-2 text-[17px] leading-relaxed text-zap-ink sm:max-w-[68%] ${mine ? "bubble-out rounded-tr-none bg-zap-bubble" : "bubble-in rounded-tl-none bg-white"}`}>
      {children}
      {msg.deleted_at ? (
        <p data-testid={`msg-deleted-${msg.id}`} className="flex items-center gap-1.5 italic text-zap-ink/60"><Ban className="h-4 w-4" />Mensagem apagada</p>
      ) : (<>
        <MessageMedia msg={msg} client={client} base={base} />
        {msg.text && <p className="whitespace-pre-wrap break-words">{msg.text}</p>}
        {msg.edited_at && <p data-testid={`msg-edited-${msg.id}`} className="text-right text-xs italic text-zap-ink/55">corrigida</p>}
      </>)}
      <div className="mt-0.5 flex items-center justify-end gap-1 text-xs text-zap-ink/55">{footer}</div>
    </div>
  </div>
);

const ReadMark = ({ msg }) =>
  msg.read_by_staff_at ? (
    <span className="flex items-center gap-0.5"><CheckCheck aria-hidden className="h-4 w-4 text-zap-green" /><span>Lida</span></span>
  ) : (
    <span className="flex items-center gap-0.5"><Check aria-hidden className="h-4 w-4" /><span>Recebido</span></span>
  );

const canDelete = (m) => m.sender === "participant" && !m.deleted_at && Date.now() - new Date(m.created_at).getTime() < 3600000;

export const DeleteButton = ({ msg, onDelete, label = "Apagar", confirmText = "Apagar esta mensagem para todos?" }) => (
  <button data-testid={`msg-delete-${msg.id}`} onClick={() => window.confirm(confirmText) && onDelete(msg.id)}
    aria-label="Apagar mensagem" className="mr-1 flex h-8 items-center gap-1 rounded-full px-2 text-xs font-semibold text-red-700 hover:bg-red-50">
    <Trash2 className="h-4 w-4" />{label}
  </button>
);

const EditButton = ({ msg, onEdit }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(msg.text || "");
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); if (await onEdit(msg.id, text)) setOpen(false); setBusy(false); };
  return (
    <>
      <button data-testid={`msg-edit-${msg.id}`} onClick={() => { setText(msg.text || ""); setOpen(true); }} aria-label="Corrigir mensagem"
        className="flex h-8 items-center gap-1 rounded-full px-2 text-xs font-semibold text-zap-deep hover:bg-zap-mist"><Pencil className="h-4 w-4" />Corrigir</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="edit-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl text-zap-deep">Corrigir mensagem</DialogTitle></DialogHeader>
          <textarea data-testid="edit-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} rows={5} autoFocus
            className="w-full resize-none rounded-lg border border-zap-line bg-zap-mist p-3 text-[17px] leading-snug outline-none focus:border-zap-green" />
          <div className="flex gap-2">
            <button data-testid="edit-cancel" onClick={() => setOpen(false)} className="h-12 flex-1 rounded-full border border-zap-line text-base font-semibold">Cancelar</button>
            <button data-testid="edit-save" onClick={save} disabled={busy || (msg.kind === "text" && !text.trim())} className="h-12 flex-1 rounded-full bg-zap-deep text-base font-bold text-white disabled:opacity-50">Salvar</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ParticipantMessages = ({ messages, client, onDelete, onEdit, onHide }) => {
  const end = useRef(null);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);
  return (
    <div data-testid="chat-history" className="py-2">
      {messages.map((m, i) => {
        const showDay = i === 0 || new Date(m.created_at).toDateString() !== new Date(messages[i - 1].created_at).toDateString();
        return (
          <Fragment key={m.id}>
            {showDay && <DaySeparator iso={m.created_at} />}
            {m.sender === "system" ? (
              <SystemBubble msg={m} />
            ) : (
              <Bubble msg={m} mine={m.sender === "participant"} client={client} base="/public/files"
                footer={<>{canDelete(m) && onEdit && (m.kind === "text" || m.text) && <EditButton msg={m} onEdit={onEdit} />}{canDelete(m) && onDelete && <DeleteButton msg={m} onDelete={onDelete} />}{m.sender === "staff" && onHide && <DeleteButton msg={m} onDelete={onHide} confirmText="Apagar esta mensagem só para você? A equipe continua com o registro." />}{fmtTime(m.created_at)}{m.sender === "participant" && !m.deleted_at && <ReadMark msg={m} />}</>}>
                {m.sender === "staff" && <p className="mb-0.5 text-sm font-semibold text-zap-green">Equipe IUDP</p>}
              </Bubble>
            )}
          </Fragment>
        );
      })}
      <div ref={end} />
    </div>
  );
};
